import { useState, useEffect, useCallback } from 'react';

interface Subscriber {
  id: string;
  email: string;
  source: string;
  created_at: string;
}

const EDGE_URL = 'https://lufjjkmzjykgvmrjucoy.supabase.co/functions/v1/admin-subscribers';

function LoginScreen({ onLogin }: { onLogin: (pwd: string) => Promise<boolean> }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const ok = await onLogin(password);
    setLoading(false);
    if (!ok) setError('Incorrect password. Please try again.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf5ff', fontFamily: "'Inter', sans-serif" }}>
      <div className="bg-white rounded-2xl border border-gray-100 p-10 w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600">
            <i className="ri-shield-keyhole-line text-white text-2xl"></i>
          </div>
          <h1 className="text-xl font-black text-gray-900 mb-1">Admin Panel</h1>
          <p className="text-xs text-gray-400">Enter your admin password to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors"
            />
          </div>
          {error && (
            <p className="text-xs text-red-500 font-medium bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap disabled:opacity-60"
          >
            {loading ? 'Verifying...' : 'Enter Admin Panel'}
          </button>
        </form>
      </div>
    </div>
  );
}

function SubscriberTable({ subscribers, onRefresh, loading }: { subscribers: Subscriber[]; onRefresh: () => void; loading: boolean }) {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  const filtered = subscribers.filter((s) =>
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  const exportCsv = () => {
    const header = 'Email,Source,Signed Up\n';
    const rows = subscribers
      .map((s) => `${s.email},${s.source},${new Date(s.created_at).toLocaleString()}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyEmails = () => {
    const emails = subscribers.map((s) => s.email).join('\n');
    navigator.clipboard.writeText(emails).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen" style={{ background: '#faf5ff', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-8 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600">
              <i className="ri-shield-keyhole-line text-white text-base"></i>
            </div>
            <div>
              <h1 className="text-base font-black text-gray-900 leading-none">Admin Panel</h1>
              <p className="text-xs text-gray-400 mt-0.5">Subscriber Management</p>
            </div>
          </div>
          <a href="/" className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-violet-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-sm"></i>
            Back to website
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-5 mb-8">
          {[
            { label: 'Total Subscribers', value: subscribers.length.toString(), icon: 'ri-user-line', color: 'from-violet-500 to-fuchsia-500' },
            { label: 'This Month', value: subscribers.filter((s) => {
              const d = new Date(s.created_at);
              const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length.toString(), icon: 'ri-calendar-line', color: 'from-fuchsia-500 to-pink-500' },
            { label: 'Today', value: subscribers.filter((s) => {
              const d = new Date(s.created_at);
              const now = new Date();
              return d.toDateString() === now.toDateString();
            }).length.toString(), icon: 'ri-time-line', color: 'from-pink-500 to-rose-500' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${stat.color} flex-shrink-0`}>
                <i className={`${stat.icon} text-white text-lg`}></i>
              </div>
              <div>
                <div className="text-2xl font-black text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Table card */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 flex-1 max-w-xs bg-gray-50 rounded-xl border border-gray-200 px-3 py-2">
              <i className="ri-search-line text-gray-400 text-sm flex-shrink-0"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email..."
                className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
                Refresh
              </button>
              <button
                onClick={copyEmails}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${copied ? 'ri-check-line text-emerald-600' : 'ri-file-copy-line'} text-sm`}></i>
                {copied ? 'Copied!' : 'Copy All Emails'}
              </button>
              <button
                onClick={exportCsv}
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 rounded-lg cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <i className="ri-download-line text-sm"></i>
                Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-loader-4-line text-violet-500 text-2xl animate-spin"></i>
                </div>
                <p className="text-sm text-gray-400">Loading subscribers...</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-user-search-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-sm text-gray-400 font-medium">
                {search ? 'No subscribers match your search' : 'No subscribers yet'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left bg-gray-50">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Signed Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((sub, i) => (
                  <tr key={sub.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-3.5 text-xs text-gray-400 font-medium">{i + 1}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex-shrink-0">
                          <span className="text-white text-xs font-bold">{sub.email[0].toUpperCase()}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-800">{sub.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-50 text-violet-700">
                        {sub.source === 'signup_modal' ? 'Sign Up Modal' : sub.source}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-xs text-gray-500">
                      {new Date(sub.created_at).toLocaleString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Footer */}
          {filtered.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-400">
                Showing <strong className="text-gray-600">{filtered.length}</strong> of <strong className="text-gray-600">{subscribers.length}</strong> subscribers
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSubscribers = useCallback(async (pwd: string) => {
    setLoading(true);
    try {
      const res = await fetch(EDGE_URL, {
        headers: { 'x-admin-password': pwd },
      });
      if (!res.ok) return false;
      const data = await res.json();
      setSubscribers(data.subscribers ?? []);
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (pwd: string): Promise<boolean> => {
    const ok = await fetchSubscribers(pwd);
    if (ok) {
      setPassword(pwd);
      setAuthed(true);
    }
    return ok;
  };

  const handleRefresh = () => {
    if (password) fetchSubscribers(password);
  };

  if (!authed) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return <SubscriberTable subscribers={subscribers} onRefresh={handleRefresh} loading={loading} />;
}
